<template>
  <section v-if="hasContent" class="trust-panel">
    <details v-if="collapsed" class="trust-panel__details">
      <summary>{{ summaryLabel }}</summary>
      <div class="trust-panel__content">
        <div class="trust-panel__summary">
          <span
            v-if="signalLabel"
            class="trust-chip-wrapper"
          >
            <span class="trust-chip trust-chip--signal">{{ signalLabel }}</span>
            <span v-if="signalTooltip" class="trust-chip-tooltip">{{ signalTooltip }}</span>
          </span>
          <span
            v-if="policyLabel"
            class="trust-chip-wrapper"
          >
            <span class="trust-chip trust-chip--policy">{{ policyLabel }}</span>
            <span v-if="policyTooltip" class="trust-chip-tooltip">{{ policyTooltip }}</span>
          </span>
          <span
            v-if="reviewLabel"
            class="trust-chip-wrapper"
          >
            <span class="trust-chip trust-chip--review">{{ reviewLabel }}</span>
            <span v-if="reviewTooltip" class="trust-chip-tooltip">{{ reviewTooltip }}</span>
          </span>
        </div>

        <div v-if="notices.length" class="trust-panel__card">
          <p class="trust-panel__label">Evidence and limits</p>
          <ul class="trust-panel__list">
            <li v-for="(notice, index) in notices" :key="`trust-notice-${index}`">{{ notice }}</li>
          </ul>
        </div>

        <div v-if="integrityIssues.length" class="trust-panel__card">
          <p class="trust-panel__label">Integrity checks</p>
          <ul class="trust-panel__list">
            <li v-for="(issue, index) in integrityIssues" :key="`trust-issue-${index}`">
              <strong>{{ issue.severityLabel }}:</strong> {{ issue.message }}
            </li>
          </ul>
        </div>

        <div v-if="showEvidenceFactors && evidenceFactors.length" class="trust-panel__card">
          <p class="trust-panel__label">Evidence factors</p>
          <ul class="trust-panel__list">
            <li v-for="(factor, index) in evidenceFactors" :key="`trust-factor-${index}`">
              <strong>{{ factor.label }}:</strong> {{ factor.value }}
            </li>
          </ul>
        </div>

      </div>
    </details>

    <template v-else>
      <div class="trust-panel__summary">
        <span
          v-if="signalLabel"
          class="trust-chip-wrapper"
        >
          <span class="trust-chip trust-chip--signal">{{ signalLabel }}</span>
          <span v-if="signalTooltip" class="trust-chip-tooltip">{{ signalTooltip }}</span>
        </span>
        <span
          v-if="policyLabel"
          class="trust-chip-wrapper"
        >
          <span class="trust-chip trust-chip--policy">{{ policyLabel }}</span>
          <span v-if="policyTooltip" class="trust-chip-tooltip">{{ policyTooltip }}</span>
        </span>
        <span
          v-if="reviewLabel"
          class="trust-chip-wrapper"
        >
          <span class="trust-chip trust-chip--review">{{ reviewLabel }}</span>
          <span v-if="reviewTooltip" class="trust-chip-tooltip">{{ reviewTooltip }}</span>
        </span>
      </div>

      <div v-if="notices.length" class="trust-panel__card">
        <p class="trust-panel__label">Evidence and limits</p>
        <ul class="trust-panel__list">
          <li v-for="(notice, index) in notices" :key="`trust-notice-${index}`">{{ notice }}</li>
        </ul>
      </div>

      <details v-if="hasDetails" class="trust-panel__details">
        <summary>Show evidence limits</summary>

        <div v-if="integrityIssues.length" class="trust-panel__card">
          <p class="trust-panel__label">Integrity checks</p>
          <ul class="trust-panel__list">
            <li v-for="(issue, index) in integrityIssues" :key="`trust-issue-${index}`">
              <strong>{{ issue.severityLabel }}:</strong> {{ issue.message }}
            </li>
          </ul>
        </div>

        <div v-if="showEvidenceFactors && evidenceFactors.length" class="trust-panel__card">
          <p class="trust-panel__label">Evidence factors</p>
          <ul class="trust-panel__list">
            <li v-for="(factor, index) in evidenceFactors" :key="`trust-factor-${index}`">
              <strong>{{ factor.label }}:</strong> {{ factor.value }}
            </li>
          </ul>
        </div>

      </details>
    </template>
  </section>
</template>

<script>
export default {
  name: 'AskTrustPanel',
  props: {
    signalLabel: {
      type: String,
      default: '',
    },
    signalTooltip: {
      type: String,
      default: '',
    },
    policyLabel: {
      type: String,
      default: '',
    },
    policyTooltip: {
      type: String,
      default: '',
    },
    reviewLabel: {
      type: String,
      default: '',
    },
    reviewTooltip: {
      type: String,
      default: '',
    },
    notices: {
      type: Array,
      default: () => [],
    },
    integrityIssues: {
      type: Array,
      default: () => [],
    },
    evidenceFactors: {
      type: Array,
      default: () => [],
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
    summaryLabel: {
      type: String,
      default: 'Show evidence and limits',
    },
    showEvidenceFactors: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    hasDetails() {
      return this.integrityIssues.length > 0 || (this.showEvidenceFactors && this.evidenceFactors.length > 0)
    },
    hasContent() {
      return Boolean(
        this.signalLabel ||
        this.policyLabel ||
        this.reviewLabel ||
        this.notices.length ||
        this.hasDetails,
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.trust-panel {
  display: flex;
  flex-direction: column;
  gap: $space-3;
  margin-bottom: $space-4;
}

.trust-panel__summary {
  display: flex;
  flex-wrap: wrap;
  gap: $space-2;
}

.trust-chip-wrapper {
  position: relative;
  display: inline-flex;
}

.trust-panel__content {
  display: flex;
  flex-direction: column;
  gap: $space-3;
  margin-top: $space-3;
}

.trust-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.35rem 0.7rem;
  font-size: $font-size-xs;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: help;
}

.trust-chip-tooltip {
  position: absolute;
  left: 50%;
  bottom: calc(100% + #{$space-2});
  z-index: 5;
  width: min(280px, 70vw);
  padding: $space-2 $space-3;
  border-radius: $radius-md;
  background: rgba(11, 17, 32, 0.96);
  color: white;
  font-size: $font-size-sm;
  font-weight: 500;
  line-height: 1.4;
  text-transform: none;
  letter-spacing: normal;
  box-shadow: 0 10px 24px rgba(11, 17, 32, 0.22);
  opacity: 0;
  visibility: hidden;
  transform: translateX(-50%) translateY(4px);
  transition: opacity 0.16s ease, transform 0.16s ease, visibility 0.16s ease;
  pointer-events: none;
}

.trust-chip-tooltip::after {
  content: "";
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translateX(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: rgba(11, 17, 32, 0.96) transparent transparent transparent;
}

.trust-chip-wrapper:hover .trust-chip-tooltip,
.trust-chip-wrapper:focus-within .trust-chip-tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

.trust-chip--signal {
  background: rgba($color-primary, 0.12);
  color: $color-primary-dark;
}

.trust-chip--policy {
  background: rgba($color-warning, 0.16);
  color: $color-text-strong;
}

.trust-chip--review {
  background: rgba($color-info, 0.12);
  color: $color-primary-deep;
}

.trust-panel__card,
.trust-panel__details {
  border: 1px solid $color-border;
  border-radius: $radius-md;
  background: $color-surface;
  padding: $space-3;
}

.trust-panel__label,
.trust-panel__details summary {
  margin: 0 0 $space-2;
  font-size: $font-size-xs;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $color-primary-dark;
}

.trust-panel__details summary {
  cursor: pointer;
}

.trust-panel__details[open] summary {
  margin-bottom: $space-3;
}

.trust-panel__list {
  margin: 0;
  padding-left: 1.2rem;
}

.trust-panel__list li + li {
  margin-top: $space-2;
}
</style>
